/**
 * YÜKLEME FORMU — "YÜKLENEN ÜRÜNLER" TABLOSUNUN SATIRLARI (saf fonksiyon).
 *
 * Belgeyi basan kod (`ShipmentDetailDrawer.printIrsaliye`) bir HTML dizesi ürettiği için
 * kural oraya gömülü kaldığında test edilemiyordu. Bu dosya YALNIZ "hangi ürün, kaç adet
 * yazılacak" kararını taşır; biçimlendirme çağıranda kalır (`sheetGrid.ts` ile aynı desen).
 *
 * ═══ İKİ KAYNAK, TEK LİSTE ═══
 * 1) SEVKİYAT SATIRLARI (atanmış lotlar) → fiilen hazır olan miktar (`hazir`).
 * 2) SİPARİŞ KALEMLERİ → hedef miktar (`hedef`). MANUEL sevkiyatta formda YAZILAN budur.
 *
 * ⚠ HİÇ PARTİSİ OLMAYAN KALEM DE LİSTEDE OLMALI (kullanıcı bildirimi, 31 Ağu 2026:
 * "ön yükleme formunda hiç parti kaydı olmayan ürün de yer almalı"). SVK-2026-00148'de
 * sipariş 2 kalemliydi, form 1'ini basıyordu: CHEFALBERTO 30CM (288 koli) hiç üretilmediği
 * için sevkiyat satırı yoktu. Kâğıtta görünmeyen kalem yükleme ekibince unutulur.
 *
 * ⚠ YALNIZ MANUEL sevkiyatta tohumlanır. Otomatik sevkiyat üretim BİTTİKTEN sonra kurulur;
 * kâğıt fiilen sevk edilen malı belgeler, oraya sıfırlık satır eklemek belgeyi yanlış yapar.
 *
 * ⚠ EŞLEŞTİRME `orderLineId` İLE, ürün koduyla DEĞİL: sevkiyat satırının birimi LOTUN taban
 * birimi, sipariş satırınınki MÜŞTERİNİN sipariş birimidir. Ürün+birim anahtarıyla
 * eşleştirilseydi ikisi farklı olduğunda aynı kalem İKİ satır olarak basılırdı.
 *
 * ⚠ Partisi olup TÜMÜ "yüklenmeyecek" (excluded) işaretlenmiş kalem TOHUMLANMAZ: onun
 * yüklenmemesi bilinçli bir karardır, sipariş miktarıyla yazmak o kararı bozar.
 */

export interface YuklemeSevkSatiri {
  orderLineId: string | null;
  itemCode: string;
  itemName: string;
  uom: string;
  qty: number;
  /** Sipariş miktarı (sevkiyat satırından gelen snapshot); null = siparişsiz/stok satırı. */
  orderedQty: number | null;
  /** Pick list'te "yüklenmeyecek" işaretli → toplama girmez. */
  excluded: boolean;
}

export interface YuklemeSiparisKalemi {
  orderLineId: string;
  itemCode: string;
  itemName: string;
  uom: string;
  orderedQty: number;
}

export interface YuklemeUrunu {
  itemCode: string;
  name: string;
  uom: string;
  /** Şu an atanmış lotlarda FİİLEN duran miktar. Partisi yoksa 0. */
  hazir: number;
  /** Siparişin bu kalem için istediği miktar (satır bazında TEK kez toplanır). */
  hedef: number;
  /** Hedefe katkı veren sipariş satırları — mükerrer toplama engeli. */
  hedefSatirlari: Set<string>;
  /** Bu ürünün hiç sevkiyat satırı (partisi) yok — form "şu an hazır: 0" der. */
  partisiYok: boolean;
}

/**
 * Formda YAZILACAK miktar.
 * Manuel planda sipariş miktarı (üretim sürerken kâğıt hedefi göstermeli — 5 Ağu 2026 kararı),
 * otomatikte fiilen yüklenen. Hedef bilinmiyorsa (siparişsiz/stok satırı) her hâlde fiili.
 */
export function formMiktari(u: YuklemeUrunu, isManual: boolean): number {
  return isManual && u.hedefSatirlari.size > 0 ? u.hedef : u.hazir;
}

export function yuklemeFormuUrunleri(girdi: {
  sevkSatirlari: YuklemeSevkSatiri[];
  /** Bu formdaki siparişlerin TÜM kalemleri (sevkiyat satırı olmayanlar dahil). */
  siparisKalemleri: YuklemeSiparisKalemi[];
  isManual: boolean;
}): YuklemeUrunu[] {
  const { sevkSatirlari, siparisKalemleri, isManual } = girdi;
  const byItem = new Map<string, YuklemeUrunu>();
  const al = (key: string, tohum: () => YuklemeUrunu) => {
    const v = byItem.get(key) ?? tohum();
    byItem.set(key, v);
    return v;
  };

  /**
   * ÖNCE TOHUMLAMA — sıra önemli: siparişin kalemleri listeye sevkiyat satırlarından ÖNCE
   * girer ki formdaki satır sırası siparişteki sırayı izlesin (yükleme ekibi kâğıdı
   * siparişle yan yana okuyor). Hazır miktarlar sonra üstüne eklenir.
   */
  if (isManual) {
    // Zaten sevkiyat satırı OLAN sipariş satırları (excluded olanlar dahil — bkz. başlık).
    const temsilEdilen = new Set(sevkSatirlari.map((l) => l.orderLineId).filter((x): x is string => !!x));
    for (const k of siparisKalemleri) {
      if (temsilEdilen.has(k.orderLineId)) continue;
      const u = al(`${k.itemCode}::${k.uom}`, () => ({
        itemCode: k.itemCode, name: k.itemName, uom: k.uom,
        hazir: 0, hedef: 0, hedefSatirlari: new Set<string>(), partisiYok: true,
      }));
      if (!u.hedefSatirlari.has(k.orderLineId)) {
        u.hedefSatirlari.add(k.orderLineId);
        u.hedef += k.orderedQty;
      }
    }
  }

  for (const l of sevkSatirlari) {
    if (l.excluded) continue; // "yüklenmeyecek" işaretli parti → forma girmez
    const u = al(`${l.itemCode}::${l.uom}`, () => ({
      itemCode: l.itemCode, name: l.itemName, uom: l.uom,
      hazir: 0, hedef: 0, hedefSatirlari: new Set<string>(), partisiYok: false,
    }));
    u.partisiYok = false; // sevkiyat satırı var
    u.hazir += l.qty;
    /**
     * ⚠ MÜKERRER SAYIM TUZAĞI: aynı sipariş satırı BİRDEN ÇOK lotla karşılanabilir (3 lot →
     * 3 sevkiyat satırı, hepsi AYNI orderLineId). Sipariş miktarını her satırda toplamak
     * 2.700'ü 8.100 yapardı; bu yüzden orderLineId bazında TEK KEZ.
     */
    if (l.orderedQty != null && l.orderLineId && !u.hedefSatirlari.has(l.orderLineId)) {
      u.hedefSatirlari.add(l.orderLineId);
      u.hedef += l.orderedQty;
    }
  }

  return [...byItem.values()];
}
